import { m, type Message } from '@/i18n';
import { mergeRankPatch, normalizeRankMap, type RankMap, type RankPatch } from './rank-order';
import {
  byWorstGrade,
  factRuleCondition,
  factRuleFires,
  parseFactRuleCore,
  type FactRule,
  type FactRuleGrade,
} from './fact-rules';
import type { Fact, FactKind } from './facts';
import {
  DEFAULT_LANE_ATTRIBUTE,
  parseLaneAttribute,
  type LaneAttributeSnapshot,
} from './lane-attributes';
import { normalizeLensView, type LensPayload, type LensRail } from './lens-view';
import { normalizeListColumns, type ListColumn, type ListSortDir } from './list-view';
import {
  normalizeCriteria,
  normalizeView,
  type CriteriaOp,
  type Criterion,
  type MapView,
} from './map-view';

/**
 * A **board**: the serializable config a `views` row of kind `board` carries
 * (docs/MODEL.md, "boards are lenses, not containers", docs/VIEWS.md §3). It
 * holds **only view-level config** and **never any work-item data**: the items
 * stay the single source of truth in the log, and the board is a stable way to
 * organize and act on them. The config is shared: everyone the board reaches
 * sees the same arrangement. (The per-viewer `pulled` focus stays transient
 * URL state, and the opener's layout choice is private attention state,
 * `src/lib/lens-rail.ts`.)
 *
 * The payload nests per layout, mirroring a view's per-rail arrangement
 * (`LensPayload`, lens-view.ts): the membership and what every layout shares
 * sit at the top (`source`, `hidden`, `showClosed`, the saved `criteria`, the
 * `factRules`, the attached view), then the owner's default `layout` and one
 * section per layout - `kanban` (its columns, the per-item crumbs that place
 * a card, its rank map and saved card sort), `list` (its columns and saved
 * column sort) and `checklist` (its top-level line order).
 *
 * `hidden` is **not** an access decision. Hiding an item on a board says "we don't
 * track this here", not "you may not see it"; the item still exists, still shows on
 * the graph and on any other board, and the access gate (who may see it at all) is
 * applied independently on top.
 *
 * A rank map (the kanban's `order`, the checklist's `order`) is the **manual
 * rank** of items on this board: a fractional-index string per item (see
 * `fractional-indexing`), so a drag writes one item's rank, a rank never has to
 * shift to make room, and concurrent drags merge rather than clobber. At read
 * time **ranked items come first by their rank**, then any item without a rank
 * tails by `createdAt`. `normalizeBoardView` drops unknown fields, so adding
 * fields later is backward-safe.
 */
/** A manual-mode lane (id minted from the label once, like field keys). */
export interface BoardLane {
  id: string;
  label: string;
  description?: string;
}

/**
 * How a board decides **what belongs on it** (docs/CONTRACT_SPINE.md §9, amended
 * 2026-07-11): the membership source, chosen at creation and editable in the
 * board's settings.
 *
 *   - `scope`   - everything granted to the board's audience, persons and
 *                 groups alike, UNION pins.
 *                 The legacy behaviour: payloads without a source normalize here,
 *                 so every pre-existing board keeps showing what it showed.
 *   - `curated` - **pins only**. The board starts empty and is populated
 *                 explicitly ("Add to board"); the birth recipe pins items
 *                 created on it even at zero pins (curation is declared, no
 *                 longer inferred from pin count alone).
 *
 * The third value, `filter` (the scope gather narrowed by `criteria`), is
 * RETIRED (docs/LENSES.md §8): a standing predicate never populates a shared
 * surface, an attached lens suggests instead (§3.3). The 0037 conversion moved
 * the data, and `normalizeBoardView` reads any stored straggler as `scope`.
 */
export type BoardSource = 'scope' | 'curated';

/**
 * One typed criterion of a board's SAVED FILTER (docs/VIEWS.md §12, T5 "Save
 * as view"): the Refine defaults a saved view opens with, never membership.
 * Deliberately the map view's `Criterion` shape (one closed set of field
 * kinds and their operators, all/any) cut to the fields below: no `group` (a
 * kanban card carries no group grants, its `groupIds` is empty in
 * src/app/boards/board.tsx), and no `party`, `tag` or delivery criteria.
 * `parent` (2026-09-15) is in: a card carries its visible parents, so a saved
 * filter may name one.
 */
export type BoardCriterion = Extract<
  Criterion,
  { field: 'closed' | 'active' | 'level' | 'assignee' | 'parent' | 'ancestor' | 'title' }
>;

// The rule vocabulary (grades, the per-fact display word, the rule-able kinds)
// and the firing predicate live in `domain/fact-rules.ts` - ONE shape in two
// jurisdictions (docs/THRESHOLDS.md §5a), so a lens and a flow can never
// disagree about what "past 14 days" means. Re-exported here because every
// board surface already imports them from the board module.
export {
  FACT_RULE_GRADES,
  FACT_RULE_WORD,
  RULE_FACT_KINDS,
  type FactRuleGrade,
} from './fact-rules';

/**
 * One lens-local a priori declaration (docs/HEALTH_SIGNALS.md §3, lens-local
 * jurisdiction): "whenever a card on THIS board carries fact X (optionally
 * while in phase P), read it at grade G". Authored board config, evaluated at
 * read time; the reading never leaves the board and never writes onto the
 * item (HS-D1). The v1 pattern scope is deliberately small - a fact kind plus
 * an optional phase condition, no free-form predicates. The type carries NO
 * name/label field: a rule keys on the `FactKind` alone (HS-A6); words enter
 * only at display time via `FACT_RULE_WORD`.
 */
export type BoardFactRule = FactRule;

/**
 * A lens grade that fired on one board entry: the machine evaluating an
 * authored a priori declaration (never declaring - the provenance chain is
 * the proof of legitimacy, HEALTH_SIGNALS §0). Lens-local by construction:
 * this shape exists only on `BoardEntry`, never on the item, its tags, or
 * any rollup (HS-D1).
 */
export interface LensSignal {
  /** The rule key that fired - the stable kind, never a display string (HS-A6). */
  fact: FactKind;
  grade: FactRuleGrade;
  /** The one-line chain: "per this board's rule: late = warning". */
  provenance: Message;
}

/**
 * Evaluate a board's fact rules against one entry's facts (SQL gathers, this
 * decides). Pure: the read layer batches `factsForItems` for the shown cards
 * and calls this per entry. A rule fires when its fact KIND is present (and
 * its phase condition, if any, holds); the fired signals come back worst
 * first, each carrying its provenance line. Nothing here reads a label,
 * a tag name, or any other display string (HS-A6).
 */
export function evaluateFactRules(
  facts: readonly Fact[],
  rules: readonly BoardFactRule[],
  item: LaneAttributeSnapshot,
): LensSignal[] {
  if (rules.length === 0 || facts.length === 0) return [];
  const fired: LensSignal[] = [];
  for (const rule of rules) {
    // The whole predicate - kind, the optional `while <value>` condition over
    // the laneable-attribute canon, and the authored threshold - is the shared
    // `factRuleFires` (domain/fact-rules.ts).
    if (!facts.some((f) => factRuleFires(f, rule, item))) continue;
    fired.push({
      fact: rule.fact,
      grade: rule.grade,
      provenance: m('facts.provenance.boardRule', {
        condition: factRuleCondition(rule),
        grade: m(`facts.grade.${rule.grade}`),
      }),
    });
  }
  return fired.sort(byWorstGrade);
}

/** The worst fired lens grade, for the card's single-dot presentation (the
 *  fold rule: worst colours, the list carries the rest). Null when none fired. */
export function worstLensSignal(signals: readonly LensSignal[]): LensSignal | null {
  return signals.length === 0 ? null : [...signals].sort(byWorstGrade)[0]!;
}

/**
 * The LAYOUTS a board is drawn in (docs/VIEWS.md §3, adopted 2026-10-26): the
 * kanban (the value `board`, the kanban rail's word in code), the list and the
 * checklist. The values are the view rails' vocabulary (`LensRail`) without
 * the map, so the URL's `?rail=`, the viewer's last choice
 * (`lens_rail_choices`) and the layout glyphs keep one vocabulary across
 * views and boards. The owner's default is `layout`, and the opener's last
 * choice wins for them (docs/LENSES.md §4 law 5).
 */
export type BoardLayout = Exclude<LensRail, 'map'>;
export const BOARD_LAYOUTS = [
  'board',
  'list',
  'checklist',
] as const satisfies readonly BoardLayout[];

export function isBoardLayout(value: unknown): value is BoardLayout {
  return (BOARD_LAYOUTS as readonly unknown[]).includes(value);
}

/**
 * The KANBAN's arrangement: what its columns mean, the manual columns, the
 * per-item crumbs that place a card (its manual column, its rank) and the
 * saved card sort. The field names are the build-time ones (`lane` for a
 * column), and the identifier lane renames them inside this namespace.
 */
export interface BoardKanbanArrangement {
  /** What the lanes MEAN (docs/VIEWS.md §3a as re-cut 2026-07-22): a
   *  laneable ATTRIBUTE key (`active` | `closed` | `delivered` | `verdict`,
   *  domain/lane-attributes.ts - the attribute's values are the lanes, and a
   *  drop applies exactly the single pair through the means compiler);
   *  `flowPhase` renders the linked `flow`'s stages (docs/FLOW.md - moving is
   *  the member's advance, a spine write); `flowField` renders the values of
   *  one of the linked flow's SELECTION fields (`laneFieldKey`; moving writes
   *  the field - logged item truth, docs/VIEWS.md slice 3); `manual` is free
   *  grouping (moving is view config). The control names what it changes.
   *  (The retired `phase`/standing lane source is converted by migration;
   *  lane-by-a-CONTEXT-field was retired 2026-07-16, docs/VIEWS.md §2a.) */
  laneSource: string;
  /** The linked flow's selection-field key the `flowField` source lanes by;
   *  null otherwise. The field's identity lives on the flow (docs/VIEWS.md
   *  §2a); the board only references it. */
  laneFieldKey: string | null;
  /** The linked FLOW definition id (docs/FLOW.md "Flows tie views together") -
   *  a soft reference to a `definitions` row (kind `flow`), resolved at read
   *  time with graceful fallback (archived/missing → phase lanes). Required by
   *  the `stage` lane source; the link itself never demands membership - the
   *  board renders non-members in a trailing unstaged lane (no container). */
  flow: string | null;
  /** Manual-mode lane definitions (unused under status/flow sources). */
  lanes: BoardLane[];
  /** A per-(board,item) crumb: manual lane assignment. With the two orders
   *  and the hidden set it is the whole of what a board records about one
   *  item. Anything more semantic than presentation must be an edge, a
   *  module datum, or a change - never another crumb here. */
  lane: Record<string, string>;
  /** Field keys the board expects filled - a presentation nudge, never a wall.
   *  An unvalidated list (the board no longer owns field definitions; historical
   *  since the context-field retirement 2026-07-16, docs/VIEWS.md §2a). */
  requires: string[];
  /** Manual order: itemId -> fractional-index rank. Items absent here have no manual
   *  rank and tail their lane by `createdAt`. Shared board config, never work-item data. */
  order: RankMap;
  /** The saved card ORDERING (docs/VIEWS.md §12, T5 "Save as view"): the sort
   *  key and direction the kanban remembers. `null` = no saved ordering (the
   *  default sort). The sort VOCABULARY lives in the view layer
   *  (`board-sort.ts`, an app-layer concern the domain must not import). The
   *  domain stores the chosen key as an OPAQUE string and `parseBoardSort` /
   *  `parseBoardDir` on the read side are the authoritative gate - an unknown
   *  value coerces to the default there, exactly like a stale URL param. */
  sort: string | null;
  dir: string | null;
}

/** The LIST layout's arrangement: the columns (list-view.ts, one validator
 *  with a view's list rail) and the saved column sort, a column key stored
 *  opaque (the view layer's parser is the gate). */
export interface BoardListArrangement {
  /** Ordered, deduped column config. Empty = the view layer's default set. */
  columns: ListColumn[];
  sort: string | null;
  dir: ListSortDir | null;
}

/** The CHECKLIST layout's arrangement (docs/CHECKLIST.md §7): the top-level
 *  line order, a sparse `itemId -> fractional-index rank` (one definition
 *  with the kanban's order, domain/rank-order.ts). Ranked lines first by
 *  rank, unranked lines tail by createdAt. A ROOTED board's top lines follow
 *  the root's order of its children instead (`setChildOrder`), so its
 *  checklist order stays empty.
 *
 *  `sort`, `dir` and `group` are the owner's saved Sort by and Group by
 *  (docs/CHECKLIST.md §3.5, 2026-10-29): `sort` null is Manual (the authored
 *  order), any other key a reading over it; `group` null is no grouping. Keys
 *  opaque (the view layer's parsers are the gate), as the list's sort is.
 *
 *  `lists` is the display option "Lists" (docs/CHECKLIST.md §2, 2026-11-03):
 *  how each member's own list stands when the board opens, `expanded`, or
 *  behind its digest (null, the default). */
export interface BoardChecklistArrangement {
  order: RankMap;
  sort: string | null;
  dir: ListSortDir | null;
  group: string | null;
  lists: 'expanded' | null;
}

export interface BoardView {
  /** What belongs on this board (see {@link BoardSource}). Absent normalizes
   *  to `scope`. */
  source: BoardSource;
  /** Item ids hidden from this board (shared board config, not per-viewer, the item
   *  untouched and still appearing elsewhere). Every layout drops them. */
  hidden: string[];
  /** Show closed items? A cross-cutting display flag, not a membership
   *  criterion (docs/VIEWS.md "Closed items"): closed items are hidden unless
   *  this is set. Default off. An ephemeral `?showClosed=1` URL override
   *  peeks past it without persisting. */
  showClosed: boolean;
  /** The saved Refine filter (docs/VIEWS.md §12, T5): the criteria the board
   *  page opens with on every layout when the URL carries none. Presentation,
   *  never membership (the retired `filter` source was the membership
   *  reading). */
  criteria: BoardCriterion[];
  /** How the criteria combine: every one must match, or any one suffices. */
  criteriaOp: CriteriaOp;
  /** Lens-local fact->grade rules (docs/HEALTH_SIGNALS.md §3; see
   *  {@link BoardFactRule}): a priori declarations this board evaluates over
   *  its own cards' facts. Absent normalizes to `[]`. */
  factRules: BoardFactRule[];
  /** The ATTACHED VIEW (docs/LENSES.md §3.3): a soft reference to a `views`
   *  row of kind `lens` whose matches render as the board's quiet suggestion
   *  lane ("matches, not pinned") to share-authority holders; admitting one is
   *  a pin - a logged act. Never membership and never access (law 1): the
   *  board's working set stays pins only. Resolved at read time with graceful
   *  fallback (retired/missing → no suggestions), like the flow link. */
  attachedLens: string | null;
  /** The owner's default layout (see {@link BoardLayout}). Absent reads as
   *  the kanban (`board`). */
  layout: BoardLayout;
  /** Each layout keeps its own arrangement, so switching layouts never loses
   *  how the board was arranged on each one (docs/LENSES.md §4's per-rail
   *  rule, on boards). */
  kanban: BoardKanbanArrangement;
  list: BoardListArrangement;
  checklist: BoardChecklistArrangement;
}

/** The membership half a board payload carries on every layout
 *  (docs/CONTRACT_SPINE.md §9 as amended): what belongs on it. The read
 *  layer's membership gathers are written once against this shape. */
export type ViewMembershipConfig = Pick<BoardView, 'source' | 'hidden' | 'showClosed'>;

export const DEFAULT_BOARD_KANBAN: BoardKanbanArrangement = {
  laneSource: DEFAULT_LANE_ATTRIBUTE,
  laneFieldKey: null,
  flow: null,
  lanes: [],
  lane: {},
  requires: [],
  order: {},
  sort: null,
  dir: null,
};

export const DEFAULT_BOARD_LIST: BoardListArrangement = { columns: [], sort: null, dir: null };

export const DEFAULT_BOARD_CHECKLIST: BoardChecklistArrangement = {
  order: {},
  sort: null,
  dir: null,
  group: null,
  lists: null,
};

export const DEFAULT_BOARD_VIEW: BoardView = {
  source: 'scope',
  hidden: [],
  showClosed: false,
  criteria: [],
  criteriaOp: 'all',
  factRules: [],
  attachedLens: null,
  layout: 'board',
  kanban: DEFAULT_BOARD_KANBAN,
  list: DEFAULT_BOARD_LIST,
  checklist: DEFAULT_BOARD_CHECKLIST,
};

/** A section of a stored payload as a record (absent or malformed = empty). */
function section(value: unknown): Record<string, unknown> {
  return (value && typeof value === 'object' && !Array.isArray(value) ? value : {}) as Record<
    string,
    unknown
  >;
}

/** A string that says something, else null (a soft reference or an opaque
 *  key). */
function opaqueString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/** A deduped string list (anything else drops). */
function stringSet(value: unknown): string[] {
  return Array.isArray(value)
    ? [...new Set(value.filter((x): x is string => typeof x === 'string'))]
    : [];
}

/**
 * The kanban section of a stored payload, coerced: lane source, flow link,
 * manual lanes and the per-item crumbs that name them, the rank map and the
 * saved card sort.
 */
export function normalizeBoardKanban(input: unknown): BoardKanbanArrangement {
  const o = section(input);
  // The flow link: a definition id (soft reference). Anything non-string reads
  // as no link; whether the id still resolves to an ACTIVE flow is a read-time
  // question (graceful fallback), not a normalization one.
  const flow = opaqueString(o.flow);

  // Lane source: a laneable attribute (default `active`) | manual | flowPhase
  // | flowField. The two flow-backed sources are only meaningful WITH a flow
  // link (and `flowField` additionally with a field key): a stored source
  // whose link is gone coerces to the default attribute here (the same
  // defence the read-time fallback gives an archived flow), so a stale
  // payload can never lane by stages/fields of nothing. Unknown keys (incl.
  // the retired `phase`/`stage` sources, converted by migration) coerce to
  // the default attribute too.
  const laneFieldKey = opaqueString(o.laneFieldKey);
  let laneSource = typeof o.laneSource === 'string' ? o.laneSource : DEFAULT_LANE_ATTRIBUTE;
  if (laneSource === 'flowPhase') {
    if (flow === null) laneSource = DEFAULT_LANE_ATTRIBUTE;
  } else if (laneSource === 'flowField') {
    if (flow === null || laneFieldKey === null) laneSource = DEFAULT_LANE_ATTRIBUTE;
  } else if (laneSource !== 'manual' && parseLaneAttribute(laneSource) === null) {
    laneSource = DEFAULT_LANE_ATTRIBUTE;
  }

  const lanes: BoardLane[] = [];
  const laneIds = new Set<string>();
  if (Array.isArray(o.lanes)) {
    for (const raw of o.lanes) {
      if (!raw || typeof raw !== 'object') continue;
      const l = raw as Record<string, unknown>;
      const id = typeof l.id === 'string' ? l.id.trim() : '';
      const label = typeof l.label === 'string' ? l.label.trim() : '';
      if (!id || !label || laneIds.has(id)) continue;
      laneIds.add(id);
      lanes.push({
        id,
        label,
        ...(typeof l.description === 'string' && l.description.trim()
          ? { description: l.description.trim() }
          : {}),
      });
    }
  }

  const lane: Record<string, string> = {};
  for (const [id, laneId] of Object.entries(section(o.lane))) {
    if (typeof laneId === 'string' && laneIds.has(laneId)) lane[id] = laneId;
  }

  return {
    laneSource,
    laneFieldKey,
    flow,
    lanes,
    lane,
    requires: stringSet(o.requires),
    // Drop any non-string rank: a bad value must never put a card in an
    // undefined slot.
    order: normalizeRankMap(o.order),
    // The saved card sort (T5): the vocabulary is not the domain's to police
    // (it lives in board-sort.ts). The read side's `parseBoardSort` /
    // `parseBoardDir` coerce any unknown value to the default, so storing the
    // raw string here is safe (like `flow`, a soft reference the domain does
    // not resolve).
    sort: opaqueString(o.sort),
    dir: opaqueString(o.dir),
  };
}

/** The list layout's section of a stored payload, coerced: the columns
 *  through the one column validator, the sort opaque, the direction gated
 *  to the closed set. */
export function normalizeBoardList(input: unknown): BoardListArrangement {
  const o = section(input);
  return {
    columns: normalizeListColumns(o.columns),
    sort: opaqueString(o.sort),
    dir: o.dir === 'asc' || o.dir === 'desc' ? o.dir : null,
  };
}

/** The checklist layout's section of a stored payload, coerced: the rank map
 *  through the one stored-order validator, the sort and grouping opaque, the
 *  direction gated to the closed set. A direction belongs to a sort: under
 *  Manual (no sort) there is none, so a stored one is dropped. */
export function normalizeBoardChecklist(input: unknown): BoardChecklistArrangement {
  const o = section(input);
  const sort = opaqueString(o.sort);
  return {
    order: normalizeRankMap(o.order),
    sort,
    dir: sort !== null && (o.dir === 'asc' || o.dir === 'desc') ? o.dir : null,
    group: opaqueString(o.group),
    lists: o.lists === 'expanded' ? 'expanded' : null,
  };
}

/**
 * Coerce arbitrary (stored or wire) input into a valid `BoardView`, filling missing
 * fields from the default and dropping anything unknown or wrongly typed - the same
 * defence map views get, so a payload from an older or hand-edited row can never put
 * a bad value on a board. Each layout's section normalizes on its own. A payload
 * without a `layout` reads as the kanban, and a stray key (the retired list
 * `form` among them) drops.
 */
export function normalizeBoardView(input: unknown): BoardView {
  const o = section(input);
  return {
    // Membership source: absent/unknown reads as `scope`. A stored retired
    // `filter` is unknown too: it reads as `scope`.
    source: o.source === 'curated' ? 'curated' : 'scope',
    hidden: stringSet(o.hidden),
    showClosed: o.showClosed === true,
    criteria: normalizeBoardCriteria(o.criteria),
    criteriaOp: o.criteriaOp === 'any' ? 'any' : 'all',
    factRules: normalizeFactRules(o.factRules),
    // The attached view (docs/LENSES.md §3.3): a soft reference like the flow
    // link - whether the id still resolves to an ACTIVE view is a read-time
    // question.
    attachedLens: opaqueString(o.attachedLens),
    layout: isBoardLayout(o.layout) ? o.layout : 'board',
    kanban: normalizeBoardKanban(o.kanban),
    list: normalizeBoardList(o.list),
    checklist: normalizeBoardChecklist(o.checklist),
  };
}

/** Validate stored fact rules: keep only well-formed entries whose kind and
 *  grade are in the closed sets, rebuild each from its typed fields (so any
 *  stray property - a `label`, a display name - is dropped, never stored:
 *  a rule keys on the FactKind alone, HS-A6), and drop anything malformed.
 *  Legacy payloads without `factRules` read as `[]` (HS-D1's additive law). */
function normalizeFactRules(input: unknown): BoardFactRule[] {
  if (!Array.isArray(input)) return [];
  const out: BoardFactRule[] = [];
  for (const raw of input) {
    const parsed = parseFactRuleCore(raw, m('flowErrors.where.boardRule'));
    if (parsed.ok) out.push(parsed.value);
  }
  return out;
}

/** Validate stored membership criteria: keep only well-formed entries with known
 *  values (the same defence the map's criteria get), and only the board's closed
 *  field set - a `group` criterion or anything unknown is dropped. */
export function normalizeBoardCriteria(input: unknown): BoardCriterion[] {
  return normalizeCriteria(input).filter(isBoardCriterion);
}

const BOARD_CRITERION_FIELDS: readonly string[] = [
  'closed',
  'active',
  'level',
  'assignee',
  'parent',
  'ancestor',
  'title',
] satisfies BoardCriterion['field'][];

/** Is this a criterion a board's filter carries? */
export function isBoardCriterion(c: Criterion): c is BoardCriterion {
  return BOARD_CRITERION_FIELDS.includes(c.field);
}

/** Assign (or clear) an item's manual kanban lane, returning the next
 *  config. Pure, like withHidden/withKanbanOrder - the write spine and tests
 *  share one definition. */
export function withLane(view: BoardView, itemId: string, laneId: string | null): BoardView {
  const lane = { ...view.kanban.lane };
  if (laneId === null) delete lane[itemId];
  else lane[itemId] = laneId;
  return { ...view, kanban: { ...view.kanban, lane } };
}

/** Toggle an item's membership in a board's hidden set, returning the next
 *  config. Pure, so the write spine and tests share one definition of "hide
 *  on a board" (`setBoardItemHidden`, every layout). */
export function withHidden<V extends { hidden: string[] }>(
  view: V,
  itemId: string,
  hidden: boolean,
): V {
  const set = new Set(view.hidden);
  if (hidden) set.add(itemId);
  else set.delete(itemId);
  return { ...view, hidden: [...set] };
}

/**
 * Merge a rank patch into the kanban's manual order, returning the next config. A
 * drag passes the one item it moved (or, when its lane had to be given ranks for the
 * first time, that whole lane) as `id -> rank`. A `null` rank clears an item's manual
 * rank so it falls back to tailing by `createdAt`. Pure and a plain merge, so the
 * write spine and tests share one definition and a concurrent drag's keys merge
 * instead of clobber. The checklist layout's own order merges through
 * `withChecklistOrder` (lens-view.ts), over the same `mergeRankPatch`.
 */
export function withKanbanOrder(view: BoardView, patch: RankPatch): BoardView {
  return { ...view, kanban: { ...view.kanban, order: mergeRankPatch(view.kanban.order, patch) } };
}

/**
 * The payload stored on a `views` row, discriminated by the row's `kind` column (kept
 * out of the JSON so the read path can filter by an indexed column). Call
 * `normalizeViewPayload(kind, payload)` to dispatch; it fails loud on unknown kinds
 * so the `stagegate` reserved-but-unbound pg enum value never silently coerces.
 */
export type ViewPayload = MapView | BoardView | LensPayload;

/**
 * Dispatch a view row's payload to the right normalizer by kind. Throws on any kind
 * not wired to a payload type so that a `stagegate` row (pg enum reserved-but-unbound,
 * the board item "Context resolution leftovers: flow drift flag, item-anchored flows,
 * saved flow-portfolio views") is never silently normalized to a `MapView` or
 * `BoardView` - the "silently coerces to map" trap that motivated removing `stagegate`
 * from `VIEW_KINDS`.
 * Callers that already filter by kind in SQL (e.g. `eq(views.kind, 'board')`) do not
 * need this, but any generic "load any view row by id" path should.
 */
export function normalizeViewPayload(kind: string, payload: unknown): ViewPayload {
  if (kind === 'board') return normalizeBoardView(payload);
  if (kind === 'map') return normalizeView(payload);
  if (kind === 'lens') return normalizeLensView(payload);
  // The retired `list` kind (every row became a board with a layout,
  // drizzle/0116_boards_take_layouts.sql) and the 'stagegate' pg enum value
  // (migration 0025) are values no write path inserts: ViewPayload has no arm
  // for them, and this throw is the loud-fail guard. When saved stage views
  // are built, add the kind here and to VIEW_KINDS and ViewPayload together.
  throw new Error(
    `Unknown view kind '${kind}': no payload normalizer is registered. ` +
      `If this is 'stagegate', it is a reserved pg enum value that is not yet wired ` +
      `(docs/STAGE_GATES.md, docs/TECH_DEBT.md).`,
  );
}
